import { useEffect, useMemo, useState } from "react";
import { db } from "../firebase/config";
import {
  collection,
  doc,
  getDoc,
  updateDoc,
  addDoc,
  serverTimestamp,
} from "firebase/firestore";
import { useAuth } from "../Context/AuthContext";
import { FaUserCircle } from "react-icons/fa";
import { Link } from "react-router-dom";
import AddressAutocomplete from "../components/AddressAutocomplete";
import { parsePlace } from "../utils/parsePlace";
import "../styles/Profile.css";

/* ---------- helpers ---------- */

const composeAddressLine = (f) =>
  [f.addressLine1, f.city, f.stateProvince, f.postalCode, f.country]
    .filter(Boolean)
    .join(", ");

const isValidPhone = (phone) =>
  !phone || /^\(?\d{3}\)?[-.\s]?\d{3}[-.\s]?\d{4}$/.test(phone);

// Geocode fallback if the user typed but never chose a suggestion
async function geocodeFallback({
  addressLine1,
  city,
  stateProvince,
  postalCode,
  country,
}) {
  const q = [addressLine1, city, stateProvince, postalCode, country]
    .filter(Boolean)
    .join(", ");
  if (!q) return null;

  try {
    const url = new URL("https://maps.googleapis.com/maps/api/geocode/json");
    url.searchParams.set("address", q);
    url.searchParams.set(
      "key",
      process.env.REACT_APP_GOOGLE_MAPS_API_KEY || "",
    );
    const res = await fetch(url.toString());
    const data = await res.json();
    if (data.status !== "OK" || !data.results?.length) return null;

    const r = data.results[0];
    const comps = r.address_components || [];
    const get = (...types) =>
      comps.find((c) => c.types?.some((t) => types.includes(t)));

    const number = get("street_number")?.long_name || "";
    const route = get("route")?.long_name || "";
    const line1 =
      number && route ? `${number} ${route}` : route || addressLine1;

    return {
      addressLine1: line1,
      city:
        get("locality")?.long_name ||
        get("sublocality")?.long_name ||
        get("administrative_area_level_2")?.long_name ||
        city,
      stateProvince:
        get("administrative_area_level_1")?.short_name ||
        get("administrative_area_level_1")?.long_name ||
        stateProvince,
      postalCode: get("postal_code")?.long_name || postalCode,
      country: get("country")?.long_name || country,
      addressGeo: {
        lat: r.geometry?.location?.lat ?? null,
        lng: r.geometry?.location?.lng ?? null,
      },
    };
  } catch {
    return null;
  }
}

/* ---------- component ---------- */

export default function Profile() {
  const { user } = useAuth();

  const [companyId, setCompanyId] = useState(null);
  const [employee, setEmployee] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [editMode, setEditMode] = useState(false);

  const [editedPhone, setEditedPhone] = useState("");

  // structured address state
  const [addressLine1, setAddressLine1] = useState("");
  const [city, setCity] = useState("");
  const [stateProvince, setStateProvince] = useState("");
  const [postalCode, setPostalCode] = useState("");
  const [country, setCountry] = useState("");
  const [addressGeo, setAddressGeo] = useState(null); // {lat,lng} | null

  /* 1) Find company that contains this employee */
  useEffect(() => {
    const fetchCompanyId = async () => {
      if (!user?.uid) return;

      try {
        setLoading(true);

        // ✅ Prefer AuthContext
        let cid = user.companyId || null;

        // ✅ Fallback: users/{uid}.companyId
        if (!cid) {
          const userSnap = await getDoc(doc(db, "users", user.uid));
          cid = userSnap.data()?.companyId || null;
        }

        if (!cid) {
          setError(
            "No company is linked to your account. Please contact your employer.",
          );
          setCompanyId(null);
          return;
        }

        setCompanyId(cid);
      } catch (e) {
        console.error(e);
        setError("Failed to fetch company ID.");
      } finally {
        setLoading(false);
      }
    };

    fetchCompanyId();
  }, [user?.uid, user?.companyId]);

  /* 2) Load employee profile and seed form fields */
  useEffect(() => {
    if (!companyId || !user) return;
    (async () => {
      try {
        setLoading(true);
        const empRef = doc(db, `companies/${companyId}/employees`, user.uid);
        const snap = await getDoc(empRef);
        if (!snap.exists()) {
          setError("Employee profile not found.");
          return;
        }
        const data = snap.data();
        setEmployee(data);

        // prefer structured object if present
        const ad = data.addressDetails || {};
        setAddressLine1(ad.line1 || data.address || data.street || "");
        setCity(ad.city || data.city || "");
        setStateProvince(ad.stateProvince || data.stateProvince || "");
        setPostalCode(ad.postalCode || data.postalCode || "");
        setCountry(ad.country || data.country || "");
        setAddressGeo(
          data.addressGeo && data.addressGeo.lat != null
            ? data.addressGeo
            : data.geo && data.geo.lat != null
            ? data.geo
            : null,
        );

        setEditedPhone(data.phone || "");
      } catch {
        setError("Failed to load profile.");
      } finally {
        setLoading(false);
      }
    })();
  }, [companyId, user]);

  const handleSave = async () => {
    if (!companyId || !user || !employee) return;

    // light validation
    if (!isValidPhone(editedPhone)) {
      alert("Please enter a valid phone number (e.g., 555-555-5555).");
      return;
    }
    if (!addressLine1 || !city || !stateProvince || !postalCode || !country) {
      alert("Please complete the address fields.");
      return;
    }

    try {
      // If user typed but didn't pick a suggestion, try to geocode
      let enriched = {};
      if (!addressGeo) {
        const fallback = await geocodeFallback({
          addressLine1,
          city,
          stateProvince,
          postalCode,
          country,
        });
        if (fallback) enriched = fallback;
      }

      const next = {
        phone: editedPhone,

        // legacy, single-line display (kept up to date)
        address: composeAddressLine({
          addressLine1: enriched.addressLine1 || addressLine1,
          city: enriched.city || city,
          stateProvince: enriched.stateProvince || stateProvince,
          postalCode: enriched.postalCode || postalCode,
          country: enriched.country || country,
        }),

        // structured source of truth
        addressDetails: {
          line1: enriched.addressLine1 || addressLine1,
          city: enriched.city || city,
          stateProvince: enriched.stateProvince || stateProvince,
          postalCode: enriched.postalCode || postalCode,
          country: enriched.country || country,
        },
        addressGeo: enriched.addressGeo || addressGeo || null,

        // OPTIONAL mirrors
        street: enriched.addressLine1 || addressLine1,
        city: enriched.city || city,
        stateProvince: enriched.stateProvince || stateProvince,
        postalCode: enriched.postalCode || postalCode,
        country: enriched.country || country,
        geo: enriched.addressGeo || addressGeo || null,
      };

      await updateDoc(
        doc(db, `companies/${companyId}/employees`, user.uid),
        next,
      );

      // reflect in UI
      setEmployee((prev) => ({ ...prev, ...next }));
      setEditMode(false);

      // notify employer
      await addDoc(collection(db, `companies/${companyId}/notifications`), {
        employeeId: user.uid,
        employeeName: employee.name || user.displayName || "Unknown",
        status: "Update Profile Info",
        type: "profileUpdate",
        message: `${employee.name ||
          "An employee"} updated their profile information`,
        timestamp: serverTimestamp(),
        read: false,
      });
    } catch (err) {
      console.error("Error saving profile:", err);
      alert("Failed to update profile.");
    }
  };

  const displayName = useMemo(() => employee?.name || "—", [employee]);
  const displayAddress = composeAddressLine({
    addressLine1,
    city,
    stateProvince,
    postalCode,
    country,
  });

  if (loading) {
    return (
      <div className="pr-wrap">
        <div className="pr-back-skel skeleton" />
        <div className="pr-card pr-header skeleton" />
        <div className="pr-grid">
          <div className="pr-card skeleton" />
          <div className="pr-card skeleton" />
          <div className="pr-card skeleton" />
        </div>
      </div>
    );
  }
  if (error) return <p style={{ color: "red", padding: 16 }}>{error}</p>;
  if (!employee) return <p style={{ padding: 16 }}>Profile not found.</p>;

  const employmentTypeLabel =
    employee?.employmentType === "part_time"
      ? "Part-time / Contract"
      : "Full-time";

  const startDateDisplay = employee?.startDate || employee?.hireDate || "—";

  const endDateDisplay =
    employee?.employmentType === "part_time"
      ? employee?.endDate || "Ongoing (no end date)"
      : "—";

  return (
    <div className={`pr-wrap ${editMode ? "pr-editing" : ""}`}>
      {/* breadcrumb */}
      <p className="pr-breadcrumb">
        <Link to="/employee-dashboard">Dashboard</Link> <span>/ Profile</span>
      </p>

      {/* header card */}
      <div className="pr-card pr-header">
        <div className="pr-avatar">
          <FaUserCircle />
        </div>

        <div className="pr-head-main">
          <h1>{displayName}</h1>
          <p className="pr-sub pr-email">{employee.email || "—"}</p>
        </div>

        <div className="pr-head-actions">
          {!editMode ? (
            <button
              className="btn-primary pr-edit-btn"
              onClick={() => setEditMode(true)}
            >
              Edit
            </button>
          ) : (
            <div className="pr-sticky-actions">
              <button className="btn-primary" onClick={handleSave}>
                Save
              </button>
              <button className="btn-ghost" onClick={() => setEditMode(false)}>
                Cancel
              </button>
            </div>
          )}
        </div>
      </div>

      {/* content grid */}
      <div className="pr-grid">
        {/* Personal */}
        <section className="pr-card">
          <h2 className="pr-section-title">Personal</h2>

          <div className="pr-field">
            <label>Full Name</label>
            <input disabled value={employee.name || ""} />
          </div>

          <div className="pr-field">
            <label>Preferred Name</label>
            <input disabled value={employee.preferredFirstName || ""} />
          </div>

          <div className="pr-field">
            <label>Username</label>
            <input disabled value={employee.username || ""} />
          </div>

          <div className="pr-field">
            <label>Gender</label>
            <input disabled value={employee.gender || ""} />
          </div>

          <div className="pr-field">
            <label>Birth Date</label>
            <input disabled value={employee.birthDate || ""} />
          </div>
        </section>

        {/* Contact */}
        <section className="pr-card">
          <h2 className="pr-section-title">Contact</h2>

          <div className="pr-field">
            <label>Phone</label>
            <input
              value={editedPhone}
              disabled={!editMode}
              onChange={(e) => setEditedPhone(e.target.value)}
              className={editMode ? "pr-input-edit" : ""}
              placeholder="(555) 555-5555"
              inputMode="tel"
            />
          </div>

          <div className="pr-field">
            <label>Address</label>

            {!editMode ? (
              <input disabled value={displayAddress || ""} />
            ) : (
              <>
                <AddressAutocomplete
                  value={addressLine1}
                  onChange={(val) => {
                    setAddressLine1(val);
                    setAddressGeo(null);
                  }}
                  onSelect={(place) => {
                    if (!place) return;
                    const p = parsePlace(place);
                    setAddressLine1(p.street || "");
                    setCity(p.city || "");
                    setStateProvince(p.stateProvince || "");
                    setPostalCode(p.postalCode || "");
                    setCountry(p.country || "");
                    setAddressGeo(
                      p.lat != null && p.lng != null
                        ? { lat: p.lat, lng: p.lng }
                        : null,
                    );
                  }}
                  placeholder="Start typing your address…"
                />

                <div className="pr-address-grid">
                  <div className="pr-field">
                    <label>City</label>
                    <input
                      value={city}
                      onChange={(e) => setCity(e.target.value)}
                      className="pr-input-edit"
                    />
                  </div>

                  <div className="pr-field">
                    <label>State / Province</label>
                    <input
                      value={stateProvince}
                      onChange={(e) => setStateProvince(e.target.value)}
                      className="pr-input-edit"
                    />
                  </div>

                  <div className="pr-field">
                    <label>Postal Code</label>
                    <input
                      value={postalCode}
                      onChange={(e) => setPostalCode(e.target.value)}
                      className="pr-input-edit"
                      inputMode="numeric"
                    />
                  </div>

                  <div className="pr-field">
                    <label>Country</label>
                    <input
                      value={country}
                      onChange={(e) => setCountry(e.target.value)}
                      className="pr-input-edit"
                    />
                  </div>
                </div>
              </>
            )}
          </div>
        </section>

        {/* Employment */}
        <section className="pr-card">
          <h2 className="pr-section-title">Employment</h2>

          <div className="pr-field">
            <label>Job Title</label>
            <input disabled value={employee.jobTitle || ""} />
          </div>

          <div className="pr-field">
            <label>Department</label>
            <input disabled value={employee.group || ""} />
          </div>

          <div className="pr-field">
            <label>Role</label>
            <input disabled value={employee.role || ""} />
          </div>

          <div className="pr-field">
            <label>Status</label>
            <input disabled value={employee.status || ""} />
          </div>

          <div className="pr-field">
            <label>Employment Type</label>
            <input disabled value={employmentTypeLabel} />
          </div>

          <div className="pr-field">
            <label>Start Date</label>
            <input disabled value={startDateDisplay} />
          </div>

          <div className="pr-field">
            <label>End Date</label>
            <input disabled value={endDateDisplay} />
          </div>
        </section>
      </div>

      {/* mobile sticky save/cancel bar (only visible while editing on mobile) */}
      {editMode && (
        <div
          className="pr-mobile-savebar"
          role="region"
          aria-label="Profile actions"
        >
          <button className="btn-primary" onClick={handleSave}>
            Save
          </button>
          <button className="btn-ghost" onClick={() => setEditMode(false)}>
            Cancel
          </button>
        </div>
      )}
    </div>
  );
}
